import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Zap, ArrowRight, Lock, Building2, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Feature cards data
const features = [
  {
    icon: Zap,
    title: "AI-Powered Classification",
    description: "GPT-powered auto-categorization with IRS Schedule-C compliance",
    delay: "0s"
  },
  {
    icon: Building2,
    title: "Multi-Entity Management",
    description: "Manage multiple LLCs and corporations from one dashboard",
    delay: "0.2s"
  },
  {
    icon: ShieldCheck,
    title: "IRS Compliance Ready",
    description: "Schedule-C categories, audit-ready reports, and tax documentation",
    delay: "0.4s"
  }
];

// Stats ticker data
const stats = [
  { label: "Transactions Classified", value: "2.5M+" },
  { label: "Companies Managed", value: "15K+" },
  { label: "Tax Reports Generated", value: "50K+" }
];

export default function LoginPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("signin");
  const [loading, setLoading] = useState(false);
  
  // Form states
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [registerName, setRegisterName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  
  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Please enter your email address");
      return;
    }
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
      toast.success("If an account exists, you'll receive a reset link shortly");
    } catch (err) {
      toast.success("If an account exists, you'll receive a reset link shortly");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/login", {
        email: loginEmail,
        password: loginPassword,
      });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("auth_token", res.data.token);  // For ProtectedRoute compatibility
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerName || !registerEmail || !registerPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    if (registerPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/register", {
        name: registerName,
        email: registerEmail,
        password: registerPassword,
      });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("auth_token", res.data.token);  // For ProtectedRoute compatibility
      toast.success("Account created! Welcome to BizLedger Pro");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="h-screen flex bg-obsidian overflow-hidden" data-testid="login-page">
      {/* Left Panel - Brand Immersion */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1639322537228-f710d846310a?q=80&w=2832&auto=format&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-obsidian/90 via-obsidian/70 to-blue-900/50" />
        
        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }} />
        </div>
        
        {/* Content - Scrollable area that fits viewport */}
        <div className="relative z-10 flex flex-col justify-between p-8 xl:p-10 w-full h-full overflow-y-auto">
          {/* Logo & Brand */}
          <div className="flex-shrink-0">
            <img 
              src="/images/bizledger-logo.png" 
              alt="BizLedger Pro" 
              className="h-20 xl:h-24 w-auto object-contain"
            />
          </div>
          
          {/* Main Value Prop */}
          <div className="space-y-4 xl:space-y-6 flex-shrink-0 py-4">
            <div>
              <p className="text-neon-cyan font-medium text-xs uppercase tracking-widest mb-2">
                Corporate Financial Operating System
              </p>
              <h2 className="text-2xl xl:text-3xl 2xl:text-4xl font-heading font-bold text-white leading-tight mb-3">
                AI-Powered Finance
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-blue to-neon-cyan">
                  for Modern Business
                </span>
              </h2>
              <p className="text-gray-400 text-sm xl:text-base max-w-md leading-relaxed">
                Transform how you manage multiple entities with intelligent transaction classification, 
                real-time bank sync, and IRS-compliant reporting.
              </p>
            </div>
            
            {/* Feature Cards - Compact with Animation */}
            <div className="space-y-2">
              {features.map((feature, idx) => (
                <div 
                  key={idx}
                  className="group bg-gray-900/40 backdrop-blur-xl border border-white/10 rounded-lg p-3 
                           hover:border-cyan-500/30 hover:bg-gray-900/60 transition-all duration-300
                           animate-float"
                  style={{ animationDelay: feature.delay }}
                  data-testid={`feature-card-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-neon-blue/20 to-neon-cyan/20 
                                  flex items-center justify-center border border-cyan-500/20
                                  group-hover:border-cyan-500/40 transition-colors flex-shrink-0">
                      <feature.icon className="w-4 h-4 text-neon-cyan" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-white font-medium text-sm">{feature.title}</h3>
                      <p className="text-gray-500 text-xs truncate">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Stats Ticker */}
          <div className="flex gap-6 pt-4 border-t border-white/10 flex-shrink-0">
            {stats.map((stat, idx) => (
              <div key={idx}>
                <p className="text-lg xl:text-xl font-heading font-bold text-white">{stat.value}</p>
                <p className="text-gray-500 text-xs">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Right Panel - Auth Terminal */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 lg:p-8 bg-obsidian overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-6">
            <img 
              src="/images/bizledger-logo.png" 
              alt="BizLedger Pro" 
              className="h-24 w-auto object-contain"
            />
          </div>
          
          {/* Auth Card */}
          <div className="bg-gray-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-heading font-bold text-white mb-2">
                {activeTab === "signin" ? "Welcome Back" : "Get Started"}
              </h2>
              <p className="text-gray-400">
                {activeTab === "signin" 
                  ? "Sign in to access your financial dashboard" 
                  : "Create your account and start your 30-day trial"}
              </p>
            </div>
            
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-black/20 p-1 rounded-lg mb-6" data-testid="auth-tabs">
                <TabsTrigger 
                  value="signin" 
                  className="data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-400 
                           data-[state=active]:border-cyan-500/50 border border-transparent rounded-md 
                           transition-all text-gray-400"
                  data-testid="signin-tab"
                >
                  Sign In
                </TabsTrigger>
                <TabsTrigger 
                  value="register" 
                  className="data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-400 
                           data-[state=active]:border-cyan-500/50 border border-transparent rounded-md 
                           transition-all text-gray-400"
                  data-testid="register-tab"
                >
                  Create Account
                </TabsTrigger>
              </TabsList>
              
              {/* Sign In Form */}
              <TabsContent value="signin">
                <form onSubmit={handleLogin} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-gray-300 text-sm">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@company.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 
                               focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all rounded-lg"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password" className="text-gray-300 text-sm">Password</Label>
                      <button
                        type="button"
                        onClick={() => {
                          setShowForgotPassword(true);
                          setForgotEmail(loginEmail);
                          setForgotSent(false);
                        }}
                        className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="Enter password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 
                               focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all rounded-lg"
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold 
                             hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all disabled:opacity-50"
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Signing in...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Sign In <ArrowRight className="w-4 h-4" />
                      </span>
                    )}
                  </Button>
                </form>
              </TabsContent>
              
              {/* Register Form */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="register-name" className="text-gray-300 text-sm">Full Name</Label>
                    <Input
                      id="register-name"
                      type="text"
                      placeholder="John Doe"
                      value={registerName}
                      onChange={(e) => setRegisterName(e.target.value)}
                      className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 
                               focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all rounded-lg"
                      data-testid="register-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email" className="text-gray-300 text-sm">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="you@company.com"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 
                               focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all rounded-lg"
                      data-testid="register-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password" className="text-gray-300 text-sm">Password</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="Min 6 characters"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 
                               focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all rounded-lg"
                      data-testid="register-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold 
                             hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all disabled:opacity-50"
                    data-testid="register-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Creating account...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Start Free Trial <ArrowRight className="w-4 h-4" />
                      </span>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            
            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-gray-900/60 px-3 text-gray-500">or continue with</span>
              </div>
            </div>
            
            {/* Google OAuth */}
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              className="w-full h-12 rounded-lg border border-white/20 bg-white/5 hover:bg-white/10 
                       text-white transition-all flex items-center justify-center gap-3"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </Button>
            
            {/* Trial Info */}
            <div className="mt-6 p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-4 h-4 text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-300 font-medium">30-Day Free Trial</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Full access to all features. No credit card required.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <p className="text-center text-gray-600 text-xs mt-6">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="forgot-password-modal">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-heading font-bold text-white">Reset Password</h3>
              <button 
                onClick={() => setShowForgotPassword(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            {!forgotSent ? (
              <>
                <p className="text-gray-400 text-sm mb-4">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
                <form onSubmit={handleForgotPassword}>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="forgot-email" className="text-gray-300 text-sm">Email Address</Label>
                      <Input
                        id="forgot-email"
                        type="email"
                        placeholder="you@company.com"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="mt-1.5 h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 
                                 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all rounded-lg"
                        data-testid="forgot-email-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-full h-12 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold"
                      data-testid="forgot-submit-btn"
                    >
                      {forgotLoading ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </span>
                      ) : (
                        "Send Reset Link"
                      )}
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <h4 className="text-white font-semibold mb-2">Check Your Email</h4>
                <p className="text-gray-400 text-sm mb-4">
                  If an account exists for <span className="text-cyan-400">{forgotEmail}</span>, you'll receive a password reset link shortly.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setShowForgotPassword(false)}
                  className="border-gray-600 text-gray-300 hover:bg-gray-800"
                >
                  Back to Login
                </Button>
              </div>
            )}
            
            <p className="text-gray-500 text-xs text-center mt-4">
              Remember your password?{" "}
              <button 
                onClick={() => setShowForgotPassword(false)}
                className="text-cyan-400 hover:text-cyan-300"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
